// One movable box on My Dashboard. In Customise mode: drag handle, up/down, full/half width, height.
import { useState } from "react";
import { GripVertical, ArrowUp, ArrowDown, Columns2, Square } from "lucide-react";
import { HEIGHTS, HEIGHT_PX } from "./useDashLayout";

const Ctl = ({ onClick, title, children, testid, on, disabled }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid} disabled={disabled}
    className={`px-1.5 py-1 rounded-md border text-[11px] inline-flex items-center gap-1 transition-colors disabled:opacity-30 ${on ? "border-teal-400/60 bg-teal-500/15 text-teal-200" : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30"}`}>{children}</button>
);

export default function ModuleFrame({ mod, index, total, title, editing, onMove, onPatch, dragId, setDragId, children }) {
  const [over, setOver] = useState(false);
  const full = mod.w === "full";
  const maxH = HEIGHT_PX[mod.h];
  const dnd = editing ? {
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", mod.id); setDragId(mod.id); },
    onDragEnd: () => { setDragId(null); setOver(false); },
    onDragOver: (e) => { if (dragId && dragId !== mod.id) { e.preventDefault(); setOver(true); } },
    onDragLeave: () => setOver(false),
    onDrop: (e) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onMove(id, index); },
  } : {};
  return (
    <section {...dnd} data-testid={`dash-module-${mod.id}`} data-width={mod.w} data-height={mod.h}
      className={`min-w-0 ${full ? "xl:col-span-2" : ""} ${editing ? "rounded-xl border border-dashed border-teal-400/35 p-1.5 bg-teal-500/[0.02]" : ""} ${over ? "ring-2 ring-teal-400/70" : ""} ${dragId === mod.id ? "opacity-40" : ""} transition-[opacity,box-shadow]`}>
      {editing && (
        <div className="flex items-center gap-1.5 px-1 pb-1.5" data-testid={`dash-module-controls-${mod.id}`}>
          <span className="cursor-grab active:cursor-grabbing text-slate-400" title="Drag to move" data-testid={`dash-module-drag-${mod.id}`}><GripVertical className="w-4 h-4" /></span>
          <span className="text-[12px] font-medium text-slate-200 truncate">{title}</span>
          <div className="ml-auto flex items-center gap-1 flex-wrap justify-end">
            <Ctl onClick={() => onMove(mod.id, index - 1)} disabled={index === 0} title="Move up" testid={`dash-module-up-${mod.id}`}><ArrowUp className="w-3 h-3" /></Ctl>
            <Ctl onClick={() => onMove(mod.id, index + 1)} disabled={index === total - 1} title="Move down" testid={`dash-module-down-${mod.id}`}><ArrowDown className="w-3 h-3" /></Ctl>
            <Ctl onClick={() => onPatch(mod.id, { w: full ? "half" : "full" })} on={full} title={full ? "Make half width" : "Make full width"} testid={`dash-module-width-${mod.id}`}>
              {full ? <Square className="w-3 h-3" /> : <Columns2 className="w-3 h-3" />}{full ? "Full" : "Half"}
            </Ctl>
            {Object.entries(HEIGHTS).map(([k, l]) => (
              <Ctl key={k} onClick={() => onPatch(mod.id, { h: k })} on={mod.h === k} title={`Height: ${l}`} testid={`dash-module-height-${mod.id}-${k}`}>{l}</Ctl>
            ))}
          </div>
        </div>
      )}
      <div style={maxH ? { maxHeight: maxH } : undefined} className={maxH ? "overflow-y-auto rounded-xl dash-scroll" : ""} data-testid={`dash-module-body-${mod.id}`}>
        {children}
      </div>
    </section>
  );
}
